import React from 'react';
import { observer, inject } from 'mobx-react';
import { when } from 'mobx';
import { DIALOGUE_TYPE } from '@/types/common';
import { isBank, isDriverOrFood } from '@/config/core';
import { classnames, getDistanceToBottom, generateOfflineFormUrl } from '@/utils/tools';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { UserEvents, UserEventStatus } from '@/stores/user/constants';
import { HotQuestionCategoryDisplayType, SourceInUrl } from '@/utils/constants';
import { getQueryForm } from '@/utils/instantLoaded/toolsWithoutDeps';
import { getButtonAction } from '@/utils/getButtonData';
import { getChatUIProviderConfig } from '@/utils/getChatUIProviderConfig';
import { isUIOptimisationHotQuestion } from '@/utils/common/UIOptimisation/HotQuestion';
import getHqUIOptimisationGroup, { HQ_UI_OPTIMISATION_GROUP } from '@/utils/experiment/getHqUIOptimisation';

import { DialogueLoader, AgentDialogueLoader } from '@/components/Common/Loader';
import { ConfigProvider } from '@shopee/chatbot-ui';
import DateTime from '@/components/DateTime';
import Dialogue from '@/components/Dialogue';
import Tip from '@/components/Tip';
import Msg from '@/components/Msg';
import HotQuestions from '@/components/HotQuestions';
import SingleArticle from '@/components/SingleArticle';
import MultiArticle from '@/components/MultiArticle';
import QAButton from '@/components/QAButton';
import Options from '@/components/Options';
import Redirection from '@/components/Redirection';
import OrderList from '@/components/OrderList';
import Order from '@/components/Order';
import Feedback from '@/components/Feedback';
import Live from '@/components/Live';
import Login from '@/components/Login';
import General from '@/components/General';
import EnquiryDetails from '@/components/EnquiryDetails';
import NegativeOptions from '@/components/NegativeOptions';
import AgentTime from '@/components/Agent/AgentTime';
import AgentEnter from '@/components/Agent/AgentEnter';
import AgentTip from '@/components/Agent/AgentTip';
import FacialVerification from '@/components/Agent/FacialVerification';
import SessionTimeout from '@/components/Agent/SessionTimeout';
import SessionTransfer from '@/components/Agent/SessionTransfer';
import UserCloseSession from '@/components/Agent/UserCloseSession';
import Queue from '@/components/Agent/Queue';
import AgentLeave from '@/components/Agent/AgentLeave';
import ChatTransfer from '@/components/Agent/ChatTransfer';
import Image from '@/components/Image';
import SessionEnd from '@/components/Agent/SessionEnd';
import UnreadMessages from '@/components/Agent/UnreadMessages';
import StateTransactionList from '@/components/StateTransactionList';
import Transaction from '@/components/Transaction';
import OrderCardWithHQ from '@/components/OrderCardWithHQ';
import FileUpload from '@/components/FileUpload';
import HistoryImage from '@/components/History/Image';
import Announcements from '@/components/Announcements';
import IntentHotQuestion from '@/components/IntentHotQuestion';
import HotQuestionCategory from '@/components/HotQuestionCategory';
import NewHotQuestion from '@/components/NewHotQuestion';
import OrderCardWithSN from '@/components/OrderCardWithSN';
import Video from '@/components/Video';
import SPLater from '@/components/SPLater';
import MySupportCase from '@/components/MySupportCase';
import ChatHistoryPrompt from '@/components/ChatHistoryPrompt';
import IntentSelector from '@/components/IntentSelector';
import SelectOrder from '@/components/SelectOrder';
import OrderCardWithButton from '@/components/ChatbotUI/OrderCardWithButton';
import KeyAnswer from '@/components/ChatbotUI/KeyAnswer';
import PopUpAnnouncementModal from '@/components/PopUpAnnouncementModal';

import LiveAgentRecommender from '@/components/LiveAgentRecommender';
import QuestionsList from '@/components/ChatbotUI/QuestionsList';
import CustomOptions from '@/components/CustomOptions';
import SystemNotice from '@/components/Notice';

import OrderSelectionFAQ from '@/components/OrderSelectionFAQ';
import OrderTipMsg from '@/components/OrderTipMsg';

import TFEMessage from '@/components/ChatbotUI/Message';
import TFESystemMessage from '@/components/ChatbotUI/SystemMessage';
import TFEOptionSelector from '@/components/ChatbotUI/OptionSelector';
import TFERelatedIntent from '@/components/ChatbotUI/RelatedIntent';
import TFEOrderList from '@/components/ChatbotUI/OrderList';
import TFEOrder from '@/components/ChatbotUI/Order';
import TFEFoodBuyerOrder from '@/components/ChatbotUI/FoodBuyerOrder';
import TFETransactionList from '@/components/ChatbotUI/TransactionList';
import TFETransaction from '@/components/ChatbotUI/Transaction';
import TFEArticle from '@/components/ChatbotUI/Article';
import TFEModal from '@/components/ChatbotUI/Modal';
import TFECancelOrder from '@/components/ChatbotUI/CancelOrder';
import TFEGuaranteeModal from '@/components/ChatbotUI/GuaranteeModal';
import TFEExpediteDeliveryModal from '@/components/ChatbotUI/ExpediteDeliveryModal';
import TFEExpediteDeliverySuccessCard from '@/components/ChatbotUI/ExpediteDeliverySuccessCard';
import TFEDeliveryInstructionCard from '@/components/ChatbotUI/DeliveryInstructionCard';
import TFEDeliveryInstructions from '@/components/ChatbotUI/DeliveryInstructions';
import TFEImage from '@/components/ChatbotUI/Image';
import OrderSelector from '@/components/ChatbotUI/OrderSelector';
import FoodOrder from '@/components/ChatbotUI/FoodOrder';
import TFETimelineCard from '@/components/ChatbotUI/TimelineCard';
import TipMessage from '@/components/ChatbotUI/TipMessage';
import TFEContactCourierCard from '@/components/ChatbotUI/ContactCourierCard';

import CaseCard from '@/components/ChatbotUI/CaseCard';
import ProbingFormPreview from '@/components/ChatbotUI/ProbingForm/Preview';

import ChannelOptionLive from '@/components/ChannelOptionLive';

import OrderListIntentSelector from '@/components/ChatbotUI/OrderListIntentSelector';
import OrderWithConfirmation from '@/components/ChatbotUI/OrderWithConfirmation';

import BasedActionHandle from '@/components/ChatbotUI/BasedActionHandle';

import SystemText from '@/components/SystemText';
import { ChannelOptionsMessage, RNPureConfigProvider } from '@shopee/chatbot-ui/rn';
import WebFormPreview from '../WebformPreview';
import FirstPageSkeleton from '../Skeleton/FirstPage';
import { AnnouncementsSkeleton, HotQuestionCategorySkeleton, MsgSkeleton, IntentHotQuestionSkeleton } from '@/components/Skeleton';
import EnquiryCaseCard from '@/components/EnquiryCaseCard';
import { FEATURE_TOGGLE_TYPE, featureToggle } from '@/utils/featureToggleConfig';

import './style.scss';
import { isDriverAndFoodUIOptimisation } from '@/utils/experiment/getDriverAndFoodUIOptimisationExperiment';

const PREFIX = 'session-area-container';

const SESSION_COMPONENTS = {
  DateTime,
  Tip,
  Msg,
  HotQuestions,
  SingleArticle,
  MultiArticle,
  QAButton,
  SelectOrder,
  Options,
  Redirection,
  OrderList,
  Order,
  Feedback,
  Live,
  Login,
  General,
  EnquiryDetails,
  NegativeOptions,
  AgentTime,
  AgentEnter,
  AgentTip,
  FacialVerification,
  SessionTimeout,
  SessionTransfer,
  UserCloseSession,
  AgentLeave,
  ChatTransfer,
  SessionEnd,
  Image,
  UnreadMessages,
  StateTransactionList,
  Transaction,
  OrderCardWithHQ,
  FileUpload,
  HistoryImage,
  Announcements,
  IntentHotQuestion,
  HotQuestionCategory,
  NewHotQuestion,
  OrderCardWithSN,
  Video,
  SPLater,
  MySupportCase,
  IntentSelector,
  LiveAgentRecommender,
  QuestionsList,
  OrderTipMsg,
  OrderSelectionFAQ,
  CustomOptions,
  SystemNotice,
  OrderCardWithButton,
  TFEMessage,
  TFESystemMessage,
  TFEOptionSelector,
  TFERelatedIntent,
  TFEOrderList,
  TFEOrder,
  TFEFoodBuyerOrder,
  TFETransactionList,
  TFETransaction,
  TFEArticle,
  TFEModal,
  TFECancelOrder,
  WebFormPreview,
  TFEGuaranteeModal,
  TFEExpediteDeliveryModal,
  TFEExpediteDeliverySuccessCard,
  TFEDeliveryInstructionCard,
  TFEDeliveryInstructions,
  TFEImage,
  TFETimelineCard,
  TipMessage,
  TFEContactCourierCard,
  CaseCard,
  OrderSelector,
  FoodOrder,
  ProbingFormPreview,
  ChannelOptionLive,
  KeyAnswer,
  OrderListIntentSelector,
  SystemText,
  AnnouncementsSkeleton,
  HotQuestionCategorySkeleton,
  MsgSkeleton,
  IntentHotQuestionSkeleton,
  OrderWithConfirmation,
  PopUpAnnouncementModal,
  BasedActionHandle,
  EnquiryCaseCard,
  ChannelOptionsMessage,
};

@inject(
  getChildInject(
    'firstInit',
    'initStore',
    'sessionStore',
    'userStore',
    'sessionIDListStore',
    'agentStore',
    'showDataStore',
    'historyStore',
    'unreadMsgStore',
  ),
)
@observer
export default class SessionArea extends React.PureComponent {
  static defaultProps = {
    // stores
    firstInit: {},
    sessionStore: {},
    userStore: {},
    sessionIDListStore: {},
    agentStore: {},
    showDataStore: {},
    unreadMsgStore: {},
  };

  constructor(props) {
    super(props);
    this.showDataListLastIdx = -1;
    this.unreadMsgListLastIdx = -1;
  }

  componentDidMount() {
    when(
      () => this.props.unreadMsgStore.messageList.length,
      () => {
        const { initStore, unreadMsgStore } = this.props;
        const { searchUrl } = initStore;
        const source = getQueryForm('__source__', searchUrl);
        const isFromPN = source === SourceInUrl.PUSH_NOTIFICATION;

        if (isFromPN && unreadMsgStore.messageList.length) {
          setTimeout(() => {
            const screenHeight = window.screen.height;
            const distanceToBottom = getDistanceToBottom();

            initStore.UPDATE_SCREEN_MAX_THEN_ONE_SCREEN(distanceToBottom > screenHeight);
          }, 0);
        }
      },
    );
  }

  // 是否已过期
  hasExpired = (dialogue) => {
    const { initStore, agentStore } = this.props;
    return agentStore.inAgent || dialogue.sessionId !== initStore.sessionId;
  };

  dialogueExtraChildrenGenerater(dialogue) {
    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];

    const isDisabled = dialogue?.data?.isDisabled; // 点过隐藏逻辑（暂时没有）

    const props = {
      ...dialogue.data,
      from: dialogue.from,
      historyDialogueId: dialogue.historyDialogueId,
      historySessionId: dialogue.historySessionId,
      dialogueId: dialogue.dialogueId,
      dialogueType: dialogue.dialogueType,
      nodePointId: dialogue.nodePointId,
      listPos: dialogue.dialogueIndex,
      isDisabled: isDisabled,
      isHistory: dialogue.isHistory || false,
      taskbotName: dialogue.taskbotName,
      threadNodeId: dialogue.threadNodeId,
      threadId: dialogue.threadId,
      threadIdV2: dialogue.threadIdV2,
      roundId: dialogue.roundId,
      sessionId: dialogue.sessionId,
      kbBotId: dialogue.kbBotId || dialogue.data.kbBotId,
      tfNodeId: dialogue.tfNodeId,
      tfCaseId: dialogue.tfCaseId,
      messageId: dialogue.message_id,
      customCallback: dialogue.customCallback,
      messageDocQATipText: dialogue.messageDocQATipText,
      useUIOptimisationRecommender: dialogue.useUIOptimisationRecommender,
      useUIOptimisationHotQuestion: dialogue.useUIOptimisationHotQuestion,
    };

    if (dialogue.type === DIALOGUE_TYPE.HotQuestionCategory) {
      if (!(dialogue.isHistory && isDriverOrFood()) && featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE, dialogue.data.displayType)) {
        return <Component {...props} />;
      }
    }

    switch (dialogue.type) {
      case DIALOGUE_TYPE.OrderList:
      case DIALOGUE_TYPE.StateTransactionList:
      case DIALOGUE_TYPE.Announcements:
      case DIALOGUE_TYPE.TFEModal:
      case DIALOGUE_TYPE.TFECancelOrder:
      case DIALOGUE_TYPE.TFEOrderList:
      case DIALOGUE_TYPE.TFETransactionList:
      case DIALOGUE_TYPE.TFEGuaranteeModal:
      case DIALOGUE_TYPE.TFEExpediteDeliveryModal:
      case DIALOGUE_TYPE.TFEDeliveryInstructions:
      case DIALOGUE_TYPE.OrderSelector:
      case DIALOGUE_TYPE.TipMessage:
        return <Component {...props} />;
      default:
        return null;
    }
  }

  dialogueChildrenGenerater(dialogue, isLastMessage = false) {
    const { isHistory = false } = dialogue;
    const { userId } = this.props.userStore;

    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];
    // 在旧逻辑中，isDisabled用于隐藏button
    // 历史消息中，添加disabledBtn用于禁止点击button
    let isDisabled = false;
    let disabledBtn = isHistory;
    switch (dialogue.type) {
      case DIALOGUE_TYPE.Live: // Live: 点过隐藏
      case DIALOGUE_TYPE.LiveAgentRecommender:
      case DIALOGUE_TYPE.ChannelOptionLive:
        isDisabled = dialogue.data.isDisabled && !isHistory;
        disabledBtn = dialogue.data.disabledBtn || isHistory;
        break;
      case DIALOGUE_TYPE.Options: // Options: session过期的会全部隐藏
        isDisabled = this.hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.QAButton: // QAButton: session过期的会全部隐藏
        isDisabled = this.hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.Feedback: // Feedback: 点过隐藏 & session过期的会全部隐藏
        isDisabled = (dialogue.data.isDisabled || this.hasExpired(dialogue)) && !isHistory;
        break;
      case DIALOGUE_TYPE.Login: // Login: 已登录的会隐藏Login按钮
        isDisabled = !!userId && !isHistory;
        break;
      case DIALOGUE_TYPE.EnquiryDetails: // EnquiryDetails: 点过隐藏
        isDisabled = dialogue.data.isDisabled && !isHistory;
        break;
      case DIALOGUE_TYPE.NegativeOptions: // NegativeOptions: 点过隐藏 & session过期的会全部隐藏
        isDisabled = (dialogue.data.isDisabled || this.hasExpired(dialogue)) && !isHistory;
        break;
      case DIALOGUE_TYPE.IntentSelector: // IntentSelector: 在intent clarification情况下点过隐藏
        isDisabled = dialogue.data.isDisabled;
        break;
      case DIALOGUE_TYPE.TFEOrderList:
        isDisabled = this.hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.OrderCardWithButton:
        isDisabled = this.hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.CaseCard:
        isDisabled = dialogue.data.isDisabled || this.hasExpired(dialogue);
        break;
    }

    const data = { ...dialogue.data };
    if (dialogue.type === 'General') {
      data.options = (dialogue.data.options || []).map((option) => {
        // 在旧逻辑中，isBtnDisabled用于隐藏button
        // 历史消息中，添加disabledBtn用于禁止点击button
        let isBtnDisabled = false;
        switch (option.button_type) {
          case 'btn_taskbot':
            isBtnDisabled = this.hasExpired(dialogue) && !isHistory;
            break;
          case 'btn_live':
            isBtnDisabled = dialogue.data.isDisabled && !isHistory;
            break;
          case 'btn_cancel_order':
            isBtnDisabled = dialogue.data.isCancelOrderDisabled && !isHistory;
            break;
          case 'btn_activate_shopeepay':
            isBtnDisabled = dialogue.data.isActivateShopeepayDisabled && !isHistory;
            break;
          case 'btn_redirection':
            if (option.uri_extra && option.uri_key === 'request_order_return_page') {
              isBtnDisabled = dialogue.data.isActivateReturnRefundDisabled && !isHistory;
            }

            if (option.uri_key.includes(generateOfflineFormUrl())) {
              isBtnDisabled = dialogue.data.isEmailusDisabled && !isHistory;
            }
            break;
          case 'btn_more_order':
            isBtnDisabled = this.hasExpired(dialogue) && !isHistory;
            break;
        }
        return {
          ...option,
          isBtnDisabled,
          disabledBtn,
          isHistory,
        };
      });
    }

    if (dialogue.type === DIALOGUE_TYPE.Options) {
      data.options = (dialogue.data.options || []).map((option) => ({ ...option, disabledBtn, isHistory }));
    }

    if (dialogue.type === DIALOGUE_TYPE.TFEMessage) {
      if (data.buttonGroup.buttons) {
        data.buttonGroup = {
          ...data.buttonGroup,
          buttons: data.buttonGroup.buttons.map((btn) => {
            let hideButton = false;
            let disabled = false;
            switch (getButtonAction(btn?.action?.action_type)) {
              case 'next':
                hideButton = this.hasExpired(dialogue) && !isHistory;
                break;
              case 'redirection':
                break;
              case 'toLiveAgent':
                // isDisabled 表示当前卡片的按钮被点击
                // hasExpired 表示dialogue已经过期，则全部隐藏
                hideButton = (btn.isDisabled || this.hasExpired(dialogue)) && !isHistory;
                break;
              case 'getIntent':
                hideButton = this.hasExpired(dialogue) && !isHistory;
                break;
              case 'cancelOrder':
                hideButton = (dialogue.data.isCancelOrderDisabled || this.hasExpired(dialogue)) && !isHistory;
                break;
              case 'extendShopeeGuarantee':
                hideButton = (dialogue.data.isExtendGuaranteeDisabled || this.hasExpired(dialogue)) && !isHistory;
                break;
              case 'expediteDelivery':
                // expediteDelivery点击一次后需要禁止再次点击
                disabled = dialogue.data.isExpediteDeliveryDisabled;
                break;
              case 'deliveryInstructions':
                // deliveryInstructions成功提交后需要禁止点击
                disabled = dialogue.data.isSubmitDeliveryInstructions;
                break;
            }

            return {
              ...btn,
              hidden: hideButton,
              isHistory,
              disabled,
            };
          }),
        };
      }
    }

    switch (dialogue.type) {
      case DIALOGUE_TYPE.OrderList:
      case DIALOGUE_TYPE.StateTransactionList:
      case DIALOGUE_TYPE.TFEModal:
      case DIALOGUE_TYPE.TFECancelOrder:
      case DIALOGUE_TYPE.TFEOrderList:
      case DIALOGUE_TYPE.TFETransactionList:
      case DIALOGUE_TYPE.TFEGuaranteeModal:
      case DIALOGUE_TYPE.TFEExpediteDeliveryModal:
      case DIALOGUE_TYPE.TFEDeliveryInstructions:
      case DIALOGUE_TYPE.OrderSelector:
      case DIALOGUE_TYPE.TipMessage:
        return null;
      default:
        const props = {
          ...data,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          nodePointId: dialogue.nodePointId,
          isDisabled: isDisabled,
          listPos: dialogue.dialogueIndex,
          from: dialogue.from,
          isHistory: dialogue.isHistory || false,
          disabledBtn,
          taskbotName: dialogue.taskbotName,
          threadNodeId: dialogue.threadNodeId,
          threadId: dialogue.threadId,
          threadIdV2: dialogue.threadIdV2,
          roundId: dialogue.roundId,
          sessionId: this.props.initStore.sessionId,
          kbBotId: dialogue.kbBotId || data.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
          isLastMessage,
          showAnimation: dialogue.showAnimation,
          messageId: dialogue.message_id,
          customCallback: dialogue.customCallback,
          messageDocQATipText: dialogue.messageDocQATipText,
          useUIOptimisationRecommender: dialogue.useUIOptimisationRecommender,
          useUIOptimisationHotQuestion: dialogue.useUIOptimisationHotQuestion,
        };
        return <Component {...props} />;
    }
  }

  dialogueBotSystemGenerater(dialogue) {
    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];

    const isDisabled = dialogue?.data?.isDisabled; // 点过隐藏逻辑（暂时没有）

    switch (dialogue.type) {
      case DIALOGUE_TYPE.TFESystemMessage:
        const props = {
          ...dialogue.data,
          from: dialogue.from,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          nodePointId: dialogue.nodePointId,
          listPos: dialogue.dialogueIndex,
          isDisabled: isDisabled,
          isHistory: dialogue.isHistory || false,
          taskbotName: dialogue.taskbotName,
          threadNodeId: dialogue.threadNodeId,
          threadId: dialogue.threadId,
          threadIdV2: dialogue.threadIdV2,
          roundId: dialogue.roundId,
          sessionId: dialogue.sessionId,
          kbBotId: dialogue.kbBotId || dialogue.data.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
          messageId: dialogue.message_id,
          customCallback: dialogue.customCallback,
          messageDocQATipText: dialogue.messageDocQATipText,
          useUIOptimisationRecommender: dialogue.useUIOptimisationRecommender,
          useUIOptimisationHotQuestion: dialogue.useUIOptimisationHotQuestion,
        };
        return <Component {...props} />;
      default:
        return null;
    }
  }

  sessionDialogueContent(dialogue, key, isLastMessage = false, needHideBubbleAngle = false, isFisrtTime = false, hiddenDatetime = false, hasHitory = false, hasGetHistoryPrompt = false) {
    // [live-chat-split-change]
    // 在原项目里（收到from为agent且有sessionId属性的为LiveChat Session，交由Live-Chat SDK渲染）
    if (dialogue.form === 'agent') {
      // 能走到这里window.LiveChatSDK理论上是一定有的，并且safeCallLiveChatSDK返回的是promise，故这边直接用window.LiveChatSDK调用即可
      return dialogue.sessionId ? window.LiveChatSDK?.renderLiveChatSession?.(dialogue.sessionId, isLastMessage) : null;
    }
    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];
    let dialogueContent = null;
    if (dialogue.from === 'system') {
      // 首屏第一条时间(firstDateTime)被announcements挡住，上拉获取历史消息or关闭 announcements 再展示
      // 对 ios 系统, datetime 再往下移动一部分以确保 msg 与 announcement 保持距离
      const u = navigator.userAgent;
      const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1;
      const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
      dialogueContent = <Component {...dialogue.data} key={key} isFisrtTime={isFisrtTime} isIOS={!isAndroid && isIOS} hiddenDatetime={hiddenDatetime} hasHitory={hasHitory} hasGetHistoryPrompt={hasGetHistoryPrompt} />;
    } else {
      if (dialogue.type === DIALOGUE_TYPE.TFESystemMessage) {
        return this.dialogueBotSystemGenerater(dialogue);
      }

      const useReversedBubble = [
        dialogue.from === 'agent' ? '' : DIALOGUE_TYPE.Order,
        DIALOGUE_TYPE.Transaction,
        DIALOGUE_TYPE.Video,
        DIALOGUE_TYPE.Image,
        DIALOGUE_TYPE.HistoryImage,
        DIALOGUE_TYPE.TFEOrder,
        DIALOGUE_TYPE.TFEFoodBuyerOrder,
        DIALOGUE_TYPE.FoodOrder,
        DIALOGUE_TYPE.EnquiryCaseCard,
        DIALOGUE_TYPE.TFETransaction].includes(dialogue.type);
      const hideDialogue = [
        DIALOGUE_TYPE.TFEModal,
        DIALOGUE_TYPE.TFECancelOrder,
        DIALOGUE_TYPE.TFEGuaranteeModal,
        DIALOGUE_TYPE.TFEExpediteDeliveryModal,
        DIALOGUE_TYPE.TFEDeliveryInstructions].includes(dialogue.type);
      // Announcements不在消息中显示只固定在顶部
      const minimizeDialogue = [DIALOGUE_TYPE.Announcements].includes(dialogue.type);

      // 2023.5.13 当modal消息不是最后一条时，不再渲染（修复进入live chat时，重新渲染chatbot消息，导致的modal再次弹出）
      const noRenderDialogue = !isLastMessage && [
        DIALOGUE_TYPE.TFEModal, DIALOGUE_TYPE.TFEGuaranteeModal, DIALOGUE_TYPE.TFEExpediteDeliveryModal].includes(dialogue.type);
      if (noRenderDialogue) {
        return null;
      }
      const renderStyle = useReversedBubble ? 'reversedBubble' : 'bubble';
      const extraChildren = this.dialogueExtraChildrenGenerater(dialogue);
      const renderChildren = dialogue.type === DIALOGUE_TYPE.Announcements || (dialogue.type === DIALOGUE_TYPE.HotQuestionCategory && dialogue.data.displayType !== HotQuestionCategoryDisplayType.OtherFrequentlyAsked);
      const children = renderChildren ? null : this.dialogueChildrenGenerater(dialogue, isLastMessage);
      // OrderList类型的消息的order列表，session过期的会全部隐藏
      const isDisabled = [DIALOGUE_TYPE.OrderList, DIALOGUE_TYPE.TFEOrderList].includes(dialogue.type) && this.hasExpired(dialogue);
      // 只展示 组件 不包裹 dialogue
      const onlyComponent = [DIALOGUE_TYPE.BasedActionHandle].includes(dialogue.type);
      // 在 video upload 需求中 图片显示了angle
      // 需要改变的话 参考下面第二行
      // const hideBubbleAngle = false;
      // const hideBubbleAngle = dialogue.type === 'Image' || dialogue.type === 'HistoryImage';
      const hideBubbleAngle
        = needHideBubbleAngle
        || dialogue.type === DIALOGUE_TYPE.TFEOrder
        || dialogue.type === DIALOGUE_TYPE.TFEContactCourierCard
        || dialogue.type === DIALOGUE_TYPE.Order
        || dialogue.type === DIALOGUE_TYPE.EnquiryCaseCard
        || dialogue.type === DIALOGUE_TYPE.WebFormPreview
        || dialogue.type === DIALOGUE_TYPE.TFEDeliveryInstructionCard
        || (dialogue.type === DIALOGUE_TYPE.NewHotQuestion && isUIOptimisationHotQuestion(dialogue?.data?.source))
        || (dialogue.type === DIALOGUE_TYPE.OrderCardWithButton
          && getHqUIOptimisationGroup() !== HQ_UI_OPTIMISATION_GROUP.CONTROL) || dialogue.type === DIALOGUE_TYPE.FoodOrder;
      // 除了 message/history-image 以外 给所有 dialogue 一个最小宽度 300px
      const noMinWidthList = [DIALOGUE_TYPE.TFEMessage, DIALOGUE_TYPE.Msg, DIALOGUE_TYPE.HistoryImage, DIALOGUE_TYPE.Video, DIALOGUE_TYPE.TFEOrder, DIALOGUE_TYPE.TFEContactCourierCard, isDriverAndFoodUIOptimisation() && DIALOGUE_TYPE.OrderSelector];
      const haveMinWidth = !noMinWidthList.includes(dialogue.type) || dialogue?.data?.haveMinWidth;
      // 填满bubble
      const showMaxWidth = [DIALOGUE_TYPE.TFEContactCourierCard, DIALOGUE_TYPE.IntentHotQuestion, DIALOGUE_TYPE.CaseCard, DIALOGUE_TYPE.EnquiryCaseCard].includes(dialogue.type) || dialogue.showMaxWidth || (dialogue.type === DIALOGUE_TYPE.Msg && dialogue.skeleton);
      // 不使用bubble的max-width
      const dontUseMaxWidth = (dialogue.type === DIALOGUE_TYPE.HotQuestionCategory && dialogue.data.displayType !== HotQuestionCategoryDisplayType.OtherFrequentlyAsked) || (dialogue.type === DIALOGUE_TYPE.Announcements && dialogue.skeleton);
      const isMinimiseCategorySmallerTab = dialogue.type === DIALOGUE_TYPE.HotQuestionCategory && dialogue.data.displayType === HotQuestionCategoryDisplayType.SmallerTab;
      // 使用max-width: 100%
      const useFullMaxWidth = [DIALOGUE_TYPE.TFEOrderList, DIALOGUE_TYPE.TFETransactionList, DIALOGUE_TYPE.OrderList].includes(dialogue.type);
      const alignCenter = isDriverAndFoodUIOptimisation() ? [DIALOGUE_TYPE.OrderSelector].includes(dialogue.type) : false;
      const dialogueProps = {
        key,
        from: dialogue.from,
        renderStyle,
        extraChildren,
        isDisabled,
        hideBubbleAngle,
        dontUseMaxWidth,
        showMaxWidth,
        hideDialogue,
        minimizeDialogue,
        useFullMaxWidth,
        showAnimation: dialogue.showAnimation,
        listPos: dialogue.dialogueIndex,
        showReloadIcon: dialogue.showReloadIcon,
        onReloadClick: dialogue.onReloadClick,
        highZIndex: dialogue?.highZIndex,
        haveMinWidth,
        onlyComponent,
        isMinimiseCategorySmallerTab,
        userInputId: dialogue?.userInputId,
        outputType: dialogue?.outputType,
        messageDocQATipText: dialogue.messageDocQATipText,
        alignCenter,
      };

      if (dialogue.feedbackInfo) {
        dialogueProps.feedbackInfo = {
          id: dialogue?.data?.id,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          listPos: dialogue.dialogueIndex,
          isHistory: dialogue.isHistory || false,
          nodePointId: dialogue.nodePointId,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          kbBotId: dialogue.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
          ...dialogue.feedbackInfo,
        };
      }

      dialogueContent = <Dialogue {...dialogueProps}>{children}</Dialogue>;
    }

    return dialogueContent;
  }

  // dialogue生成器
  dialogueGenerater(dialogue, index, isLastMessage = false, needHideBubbleAngle = false, isFisrtTime = false, hiddenDatetime = false, hasHitory = false, hasGetHistoryPrompt = false) {
    if (dialogue.hidden) {
      return null;
    }

    let dialogueContent = null;
    const key = `dialogue-${index}`;
    dialogueContent = this.sessionDialogueContent(dialogue, key, isLastMessage, needHideBubbleAngle, isFisrtTime, hiddenDatetime, hasHitory, hasGetHistoryPrompt);

    return dialogueContent;
  }

  setLastMessage() {
    this.showDataListLastIdx = -1;
    this.unreadMsgListLastIdx = -1;
    const { unreadMsgStore, showDataStore } = this.props;
    const { messageList: unreadMsgList } = unreadMsgStore;
    const { dataList } = showDataStore;
    const lastList = dataList?.length > 0 ? dataList : unreadMsgList;
    const lastIdx = Math.max(0, lastList?.length - 1);
    const lastItem = lastList[lastIdx];
    if (!lastItem) {
      return;
    }
    if (dataList?.length > 0) {
      this.showDataListLastIdx = lastIdx;
    } else {
      this.unreadMsgListLastIdx = lastIdx;
    }
  }

  render() {
    const { firstInit, sessionStore, showDataStore, agentStore, initStore, historyStore, userStore, unreadMsgStore } = this.props;

    const { typingFlag, position, initialPosition, inQueue, loadingInAgent } = agentStore;

    const { isLoading } = sessionStore;

    const { dataList } = showDataStore;
    const { historyList } = historyStore;
    const { userEvents } = userStore;
    const { messageList: unreadMsgList } = unreadMsgStore;
    this.setLastMessage();
    const historyDialogues = historyList.map((dialogue, index) => this.dialogueGenerater(dialogue, dialogue.message_id, false, index > 0 && historyList[index - 1]?.from === dialogue?.from));
    const unreadDialogues = unreadMsgList.map((dialogue, index) => this.dialogueGenerater(dialogue, dialogue.message_id, index === this.unreadMsgListLastIdx, index > 0 && unreadMsgList[index - 1]?.from === dialogue?.from));
    // 所有消息中的第一条msg
    const firstMsgIndex = dataList.findIndex(dialogue => isBank() ? (dialogue.type === DIALOGUE_TYPE.Msg || dialogue.type === DIALOGUE_TYPE.Live) : dialogue.type === DIALOGUE_TYPE.Msg);
    // 如果没有 announcements 需要隐藏 first datetime
    const announcements = initStore?.initAnnouncementsData;
    const hiddenDatetime = historyDialogues?.length === 0 && announcements?.length === 0;
    // 有历史消息需要调整 first datetime 间距
    const hasHitory = historyDialogues?.length !== 0;
    // 如果有 GetHistoryPrompt 需要调整 first datetime 间距
    const hasGetHistoryPrompt = userEvents[UserEvents.GetHistoryPrompt] === UserEventStatus.NonExist;
    // 根据 form 判断对话内容来源判断,不一致即为单边对话中的第一条 msg(需要展示 BubbleAngle)
    const dialogues = dataList.map((dialogue, index) => this.dialogueGenerater(dialogue, index, index === this.showDataListLastIdx, (index > firstMsgIndex && dataList[index - 1]?.from === dialogue?.from) || index < firstMsgIndex, index === 0, hiddenDatetime, hasHitory, hasGetHistoryPrompt));
    // [live-chat-split-change]
    const LoaddingInAgentQueue = window?.LiveChatSDK?.renderLoaddingInAgent;
    return (
      <div
        className={classnames(PREFIX, {
          'no-bottom-bar': initStore.shouldBottomBarHidden,
          'sessions-container__agent': agentStore.inAgent,
          'with-input-more': agentStore.isOpenMore,
        })}
        id="session-area-container-id"
      >
        <div className="sessions-container">
          <RNPureConfigProvider {...getChatUIProviderConfig()}>
            <ConfigProvider {...getChatUIProviderConfig()}>
              {historyDialogues}
              {userEvents[UserEvents.GetHistoryPrompt] === UserEventStatus.NonExist && <ChatHistoryPrompt />}
              {unreadDialogues}
              {dialogues}
              {firstInit.initStore.showSkeleton && <FirstPageSkeleton />}
            </ConfigProvider>
          </RNPureConfigProvider>
          {isLoading && (
            <Dialogue from="bot">
              {isBank() ? <AgentDialogueLoader /> : <DialogueLoader />}
            </Dialogue>
          )}
          {loadingInAgent && LoaddingInAgentQueue ? <LoaddingInAgentQueue /> : null}
          {typingFlag && (
            <Dialogue from="agent">
              <AgentDialogueLoader />
            </Dialogue>
          )}
        </div>
      </div>
    );
  }
}
